/**
 * The URL grammar (A-41), a public contract that changes only additively:
 *
 *   <base>level/<id>
 *
 * Only the level is addressable. Selection and camera stay out of the address
 * (`sel=`/`cam=` were removed; see the decisions log). Unknown parameters are
 * ignored, never fatal. A path forwarded by GitHub Pages' 404 shim arrives as
 * `?redirect=<path>` and is restored here (SPEC §9).
 */

export interface Route {
  readonly levelId: string | null;
}

export interface ParsedLocation {
  readonly route: Route;
  /** The route came through `?redirect=` and the address bar should be rewritten. */
  readonly redirected: boolean;
}

/** Level ids are content slugs (A-27). */
const LEVEL_ID = /^[a-z0-9][a-z0-9-]*$/;

export function parseLocation(pathname: string, search: string, base: string): ParsedLocation {
  const redirect = new URLSearchParams(search).get("redirect");
  if (redirect) {
    // The shim encodes the original path and query; resolve them against a dummy origin.
    const original = new URL(redirect, "https://redirect.invalid");
    return { route: { levelId: levelIdFrom(original.pathname, base) }, redirected: true };
  }
  return { route: { levelId: levelIdFrom(pathname, base) }, redirected: false };
}

export function formatRoute(route: Route, base: string): string {
  return route.levelId === null ? base : `${base}level/${route.levelId}`;
}

/**
 * The address the 404 shim forwarded (`?redirect=…`), if any. Only local paths
 * are accepted: never another origin, and not `//host` (protocol-relative).
 * The app restores it before anything reads the URL (`?debug`, `?rhi=`).
 */
export function redirectedUrl(search: string): string | null {
  const target = new URLSearchParams(search).get("redirect");
  if (!target?.startsWith("/") || target.startsWith("//")) return null;
  return target;
}

/** Query parameters the router consumes; everything else (`?debug`, `?rhi=`) is the visitor's. */
const OWNED_PARAMS: ReadonlySet<string> = new Set(["redirect"]);

/**
 * Appends the current address's foreign query parameters to a formatted route,
 * verbatim, so opening a level never drops `?debug` or `?rhi=webgl`.
 */
export function withForeignParams(url: string, currentSearch: string): string {
  const foreign = currentSearch
    .replace(/^\?/, "")
    .split("&")
    .filter(
      (part) => part !== "" && !OWNED_PARAMS.has(decodeURIComponent(part.split("=")[0] ?? "")),
    );
  if (foreign.length === 0) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${foreign.join("&")}`;
}

function levelIdFrom(pathname: string, base: string): string | null {
  if (!pathname.startsWith(base)) return null;
  const [section, id, ...rest] = pathname.slice(base.length).split("/");
  if (section !== "level" || id === undefined || rest.some((part) => part !== "")) return null;
  return LEVEL_ID.test(id) ? id : null;
}
